/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import { useClickAway } from '@uidotdev/usehooks';
import {
  createContext,
  type PropsWithChildren,
  useMemo,
  type ReactNode,
  useContext,
  useState,
  useCallback,
} from 'react';

type SearchDropdownState = {
  animationIsDone: () => void;
  closeDropdown: () => void;
  dropdownState: 'opened' | 'closed' | 'transitioning';
  openDropdown: () => void;
};

const SearchDropdownContext = createContext<SearchDropdownState>({
  animationIsDone() {},
  closeDropdown() {},
  dropdownState: 'closed',
  openDropdown() {},
});

/**
 * Provides and manages the dropdown state.
 *
 * The state transitions should be: 'closed' -> 'opened' -> 'transitioning' -> 'closed'
 * The `transitioning` state is meant to allow various animations actions to complete before
 * transitioning the state to `closed`.
 */
export function SearchDropdownProvider({ children }: PropsWithChildren): ReactNode {
  const [dropdownState, setState] = useState<SearchDropdownState['dropdownState']>('closed');

  const closeDropdown = useCallback(() => {
    setState('transitioning');
  }, [setState]);

  const openDropdown = useCallback(() => {
    setState('opened');
  }, [setState]);

  const animationIsDone = useCallback(() => {
    setState('closed');
  }, [setState]);

  const ref = useClickAway<HTMLDivElement>(() => {
    if (dropdownState === 'opened') {
      closeDropdown();
    }
  });

  const state = useMemo(() => {
    return { animationIsDone, closeDropdown, dropdownState, openDropdown };
  }, [animationIsDone, closeDropdown, dropdownState, openDropdown]);

  return (
    <div ref={ref}>
      <SearchDropdownContext.Provider value={state}>{children}</SearchDropdownContext.Provider>
    </div>
  );
}

export function useSearchDropdown(): SearchDropdownState {
  return useContext(SearchDropdownContext);
}
